import React, { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { colors2, typoV2_CSS } from "oolib";
import { List } from "@/components/UI/List";
import { Lightbox, LightboxContent } from "@/components/UI/lightbox";
import LexicalTextEditor from "@/components/inputs/TextEditor/LexicalTextEditor";

// Wrapper around the read-only editor so the rich quote sits flush in the row
// (no editor chrome / paragraph margins bleeding into the list item).
const Quote = styled.div`
  line-height: 1.55;
  cursor: pointer;

  & p {
    margin: 0;
  }

  /* The read-only editor sets cursor:text on its content region; force the
     row's pointer cursor through so the whole item reads as clickable. */
  & * {
    cursor: pointer;
  }
`;

// The rich quote clamped to $lines lines. One element does double duty: it's
// what we measure (scrollHeight vs clientHeight) AND what we display.
const ClampBox = styled(Quote)`
  display: -webkit-box;
  -webkit-line-clamp: ${({ $lines }) => $lines};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// The quote's END on a single line, shown as line 2 when the quote is taller
// than two lines. Plain text (the rich render can't be reliably sliced to "just
// the last line"), with the ellipsis rendered at the START via the rtl trick so
// the tail of the sentence — not its middle — stays visible.
const Tail = styled.div`
  /* Match line 1's typography exactly — the editor renders its paragraphs with
     UI_BODY_SM_DF (see LexicalTextEditor styled.js), so the tail must too, or it
     reads smaller than the line above it. */
  ${typoV2_CSS.UI_BODY_SM_DF};
  color: ${colors2.grey80};
  line-height: 1.55;
  direction: rtl;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
`;

// ── "badges" variant ────────────────────────────────────────────────────────
// Compact alternate: one lettered badge (A, B, …) per overlapping annotation
// instead of full-text rows. Hovering a badge highlights its mark in the doc
// (so the letters stay identifiable), clicking picks it.
const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem;
`;

const Badge = styled.button`
  appearance: none;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.6rem;
  height: 3.6rem;
  border: none;
  border-radius: 0.8rem;
  background: #000;
  color: #FFF;
  ${typoV2_CSS.UI_BODY_BOLD};
  cursor: pointer;
  user-select: none;

  /* Keyboard focus only (no ring after a mouse click), offset 0 so it hugs the
     badge instead of bleeding into its neighbour. */
  &:focus {
    outline: none;
  }
`;

// Strip the Lightbox's card chrome for the badges variant. The class lands on
// the outer panel (StyledLightboxPanel) and we null out the white background,
// shadow, border, padding and min-width on it + its inner content panel, so only
// the bare badges show — matching the inline cluster in the design.
const BareLightboxContent = styled(LightboxContent)`
  background: transparent !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  min-width: 0 !important;

  & > div {
    background: transparent !important;
    border: none !important;
    padding: 4px !important;
  }
`;

// A → Z then wrap; overlaps deep enough to exhaust the alphabet are not a real
// case, but wrapping keeps the label deterministic rather than throwing.
const badgeLabel = (i) => String.fromCharCode(65 + (i % 26));

const FullTextList = styled.div`
  /* The List's root carries the maxHeight + overflow (passed via its style), so
     it owns the scroll. Give that scrollbar a thin, subtle look instead of the
     default chunky one. */
  & > div {
    scrollbar-width: thin;
    scrollbar-color: ${colors2.grey20} transparent;

    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: ${colors2.grey20};
      border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: ${colors2.grey30};
    }
  }

  /* Square the rows so the full-width dividers between them read cleanly
     (the List's default rounded-chip hover otherwise fights with dividers). */
  .OKE-Dropdown__option {
    border-radius: 0;
  }
  .OKE-Dropdown__option + .OKE-Dropdown__option {
    border-top: 1px solid ${colors2.grey10};
  }

  /* Fallback for rows without a rich fragment (plain-text display): show the
     full annotated text instead of the List's default 2-line clamp. */
  .OKE-Dropdown__optionText {
    display: block !important;
    -webkit-line-clamp: unset !important;
    -webkit-box-orient: unset !important;
    overflow: visible !important;
    text-overflow: clip !important;
    white-space: normal !important;
  }
`;

// Read-only rich render of an annotation's quote — same approach AnnoCard uses,
// so the annotated text keeps its original formatting (breaks, emphasis, etc.).
const editorEl = (fragment) => (
  <LexicalTextEditor
    variant="rich"
    readOnly
    v2
    disableViewHighlights
    disableCreateHighlights
    value={fragment}
  />
);

// Show the quote in at most two lines. Within that budget it renders whole, with
// formatting intact. Past two lines it middle-truncates: the formatted first
// line, then the sentence's END on the second line — so both ends stay readable
// (mirrors the start…end shape the design asks for).
const ClampedQuote = ({ fragment, excerpt }) => {
  const ref = useRef(null);
  const [overflow, setOverflow] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // overflow:hidden + line-clamp don't shrink scrollHeight, so it's the full
    // content height; taller than the clamp box ⇒ more than the clamped lines.
    const check = () => setOverflow(el.scrollHeight - el.clientHeight > 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fragment, excerpt]);

  return (
    <div>
      <ClampBox ref={ref} $lines={overflow ? 1 : 2}>
        {editorEl(fragment)}
      </ClampBox>
      {overflow && excerpt ? <Tail>{excerpt}</Tail> : null}
    </div>
  );
};

// Full-text list rows — the original picker UI.
const ListBody = ({ items, onPick, onHoverItem }) => {
  const options = items.map((it) => ({
    value: it.dataId,
    // `display` is the plain-text fallback + the row's title tooltip.
    display: it.excerpt || "Annotation",
    // `displayNode` renders the rich quote (clamped to two lines) when we have
    // the lexical fragment.
    displayNode: it.fragment ? (
      <ClampedQuote fragment={it.fragment} excerpt={it.excerpt} />
    ) : undefined,
    // Hovering a row highlights the corresponding annotation mark in the doc.
    onHoverChange: (hovered) => onHoverItem?.(hovered ? it.dataId : null),
  }));

  return (
    <LightboxContent
      className="js-anno-keep-focus"
      title={`${items.length} annotations here`}
      style={{ width: "24rem" }}
    >
      <FullTextList>
        <List
          id="annoOverlapPicker"
          options={options}
          value={null}
          focussedOp={-1}
          scrollFocussedOpIntoView={() => {}}
          onChange={(_id, option) => onPick(option.value)}
          disableShadow
          style={{ maxHeight: "20rem", overflowY: "auto" }}
        />
      </FullTextList>
    </LightboxContent>
  );
};

// Lettered badges — the compact alternate UI.
const BadgesBody = ({ items, onPick, onHoverItem }) => (
  // initialFocus={-1}: don't auto-focus the first badge on open (otherwise it
  // shows a focus ring at rest, unlike its neighbours).
  <BareLightboxContent className="js-anno-keep-focus" initialFocus={-1}>
    <BadgeRow>
      {items.map((it, i) => (
        <Badge
          key={it.dataId}
          type="button"
          onMouseEnter={() => onHoverItem?.(it.dataId)}
          onMouseLeave={() => onHoverItem?.(null)}
          onFocus={() => onHoverItem?.(it.dataId)}
          onBlur={() => onHoverItem?.(null)}
          onClick={() => onPick(it.dataId)}
        >
          {badgeLabel(i)}
        </Badge>
      ))}
    </BadgeRow>
  </BareLightboxContent>
);

/**
 * Overlapping-annotation picker — V2 only.
 *
 * A Lightbox anchored to the clicked <mark>, with two interchangeable bodies
 * (`variant`): "list" — one full-text row per overlapping annotation (rich
 * quote, like AnnoCard); "badges" — a compact strip of lettered badges (A, B,
 * …). `items` arrive PRE-SORTED by specificity (shortest annotated span first)
 * — see the ranking in AnnoPlugin's select handler. Either way, hovering
 * previews the matching mark and picking hands its annoDataId back to the host.
 */
export const AnnoOverlapPicker = ({ anchorEl, items, onPick, onClose, onHoverItem, variant = "list" }) => {
  return (
    <Lightbox
      open
      anchorEl={anchorEl}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      placement="bottom-start"
    >
      {variant === "badges" ? (
        <BadgesBody items={items} onPick={onPick} onHoverItem={onHoverItem} />
      ) : (
        <ListBody items={items} onPick={onPick} onHoverItem={onHoverItem} />
      )}
    </Lightbox>
  );
};

export default AnnoOverlapPicker;
